import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CreateRoleRequest,
  PermissionDto,
  RoleDto,
  UpdateRoleRequest,
} from './types';

const QK_ROLES = ['roles'] as const;
const QK_PERMISSIONS = ['permissions'] as const;

export function useRoles() {
  return useQuery({
    queryKey: QK_ROLES,
    queryFn: async () => (await apiClient.get<RoleDto[]>('/api/roles')).data,
  });
}

export function usePermissions() {
  return useQuery({
    queryKey: QK_PERMISSIONS,
    queryFn: async () => (await apiClient.get<PermissionDto[]>('/api/permissions')).data,
    // The permission catalog is platform-wide and barely changes — cache it
    // for an hour so opening the role editor doesn't trigger another fetch.
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateRoleRequest) =>
      (await apiClient.post<RoleDto>('/api/roles', request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_ROLES }),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: UpdateRoleRequest }) =>
      (await apiClient.put<RoleDto>(`/api/roles/${id}`, request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_ROLES }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/roles/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_ROLES }),
  });
}
